/**
 * Container for the mobile Share full-screen sheet (PRD-6.1 / SE-8313) — the
 * mobile sibling of the desktop ShareWatchlistModal.
 *
 * Thin adapter: forwards open/identity props into the shared
 * {@link useShareWatchlist} hook (which owns all Apollo data and grant writes —
 * the same hook backing the desktop modal) and spreads the hook's render state
 * onto the mobile sheet View. Dismissal (✕ / backdrop / swipe-down / ESC) is
 * handled by the MobileSheetChrome primitive composed in the View — this
 * Container only forwards `onClose`.
 * @module features/scouting/components/MobileShareWatchlistSheet/MobileShareWatchlistSheetContainer
 */

import MobileShareWatchlistSheetView from "./MobileShareWatchlistSheetView";

import { useShareWatchlist } from "../../../hooks/useShareWatchlist";

/** Props for `MobileShareWatchlistSheetContainer`. */
export interface MobileShareWatchlistSheetContainerProps {
  /** Whether the sheet is open. Gates the Apollo queries via the hook's skip. */
  readonly isOpen: boolean;
  /** Watchlist id being shared. */
  readonly watchlistId: string;
  /** Watchlist name (sheet title + toast copy). */
  readonly listName: string;
  /** Dismiss handler — fired by Done and by the chrome's ✕/backdrop/swipe/ESC. */
  readonly onClose: () => void;
}

/**
 * Renders the mobile Share full-screen sheet, sourcing all state from
 * useShareWatchlist.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the sheet is open
 * @param root0.watchlistId - Watchlist id being shared
 * @param root0.listName - Watchlist name
 * @param root0.onClose - Dismiss handler
 * @returns The mobile Share sheet view.
 */
const MobileShareWatchlistSheetContainer = ({
  isOpen,
  watchlistId,
  listName,
  onClose,
}: MobileShareWatchlistSheetContainerProps) => {
  const share = useShareWatchlist({ isOpen, watchlistId, listName, onClose });

  return (
    <MobileShareWatchlistSheetView
      isOpen={isOpen}
      listName={listName}
      accessRows={share.accessRows}
      accessState={share.accessState}
      hasSharees={share.hasSharees}
      searchQuery={share.searchQuery}
      candidates={share.candidates}
      selectedCandidate={share.selectedCandidate}
      inviteRole={share.inviteRole}
      inviteError={share.inviteError}
      isInviting={share.isInviting}
      onSearchChange={share.onSearchChange}
      onSelectCandidate={share.onSelectCandidate}
      onClearCandidate={share.onClearCandidate}
      onInviteRoleChange={share.onInviteRoleChange}
      onInvite={share.onInvite}
      onChangeRole={share.onChangeRole}
      onRevoke={share.onRevoke}
      groupCandidate={share.groupCandidate}
      selectedGroup={share.selectedGroup}
      onSelectGroup={share.onSelectGroup}
      groupRows={share.groupRows}
      onChangeGroupRole={share.onChangeGroupRole}
      onRevokeGroup={share.onRevokeGroup}
      onClose={share.onDone}
    />
  );
};

MobileShareWatchlistSheetContainer.displayName =
  "MobileShareWatchlistSheetContainer";

export default MobileShareWatchlistSheetContainer;
